import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Spin, Alert, Tag } from 'antd';
import { IconFileExport } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getAdminGdprRequests, IAdminGdprRequest } from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, dateSorter } from '../../../helpers/adminTableHelpers';

const { Title } = Typography;

// Queue of "export my data" requests raised from Configuración > Privacidad.
// "Delete my account" isn't listed here: that request already lives entirely
// in AuthorizationApi's own database, which this app has no bridge to yet
// (same parked cross-database question as Usuarios/Verificación de
// edad/Roles y permisos) -- see GdprRequest's own doc comment.
const GdprRequestsSection: React.FC = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<IAdminGdprRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadRequests = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminGdprRequests()
      .then(setRequests)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_GDPR)}
      </Title>

      <Alert
        type="info"
        showIcon
        message={translate(t, TRANSLATIONS.ADMIN_GDPR_DELETE_REQUESTS_NOTE)}
        style={{ marginBottom: 20 }}
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_GDPR_LOAD_ERROR)} />
      ) : requests.length === 0 ? (
        <EmptyState
          icon={<IconFileExport size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_GDPR_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_GDPR_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminGdprRequest>
          rowKey="id"
          dataSource={requests}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.id,
            title: record.userDisplayName,
            subtitle: translate(t, TRANSLATIONS.ADMIN_GDPR_TYPE_EXPORT),
            badge: <Tag color="green" style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_GDPR_STATUS_COMPLETED)}</Tag>,
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_GDPR_COL_REQUESTED_AT), value: new Date(record.createdAt).toLocaleString(), wide: true },
              {
                label: translate(t, TRANSLATIONS.ADMIN_GDPR_COL_PROCESSED_AT),
                value: record.processedAt ? new Date(record.processedAt).toLocaleString() : '—',
                wide: true,
              },
            ],
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_GDPR_COL_USER),
              dataIndex: 'userDisplayName',
              key: 'userDisplayName',
              sorter: textSorter<IAdminGdprRequest>('userDisplayName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_GDPR_COL_TYPE),
              dataIndex: 'requestType',
              key: 'requestType',
              render: () => translate(t, TRANSLATIONS.ADMIN_GDPR_TYPE_EXPORT),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_GDPR_COL_STATUS),
              dataIndex: 'status',
              key: 'status',
              render: () => <Tag color="green">{translate(t, TRANSLATIONS.ADMIN_GDPR_STATUS_COMPLETED)}</Tag>,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_GDPR_COL_REQUESTED_AT),
              dataIndex: 'createdAt',
              key: 'createdAt',
              sorter: dateSorter<IAdminGdprRequest>('createdAt'),
              defaultSortOrder: 'descend',
              render: (value: string) => new Date(value).toLocaleString(),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_GDPR_COL_PROCESSED_AT),
              dataIndex: 'processedAt',
              key: 'processedAt',
              sorter: dateSorter<IAdminGdprRequest>('processedAt'),
              render: (value?: string) => (value ? new Date(value).toLocaleString() : '—'),
            },
          ]}
        />
      )}
    </div>
  );
};

export default GdprRequestsSection;
